import { useState, useEffect, useRef } from "react";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { Plus, Download, Save, RefreshCw, Send, ChevronDown } from "lucide-react";
import AdminNavbar from "../../components/layout/AdminNavbar";
import MultiPagePdfDocument from "../../components/admin/MultiPagePdfDocument";
import api from "../../api/axios";

const GeneralLetters = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [fieldValues, setFieldValues] = useState({});
    const [letterBody, setLetterBody] = useState("");
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [message, setMessage] = useState("");
    const [recipientEmail, setRecipientEmail] = useState("");

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        loadTemplates();
    }, []);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const loadTemplates = async () => {
        try {
            const res = await api.get("/api/admin/general-letter-templates");
            const sortedTemplates = res.data.map(t => ({ ...t, name: t.title || t.name, defaultBody: t.body || t.defaultBody })).sort((a, b) => a.name.localeCompare(b.name));
            setTemplates(sortedTemplates);
            if (sortedTemplates.length > 0) {
                selectTemplate(sortedTemplates[0]._id, sortedTemplates);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error loading templates", error);
            setLoading(false);
        }
    };

    const selectTemplate = (id, templateList = templates) => {
        setSelectedTemplateId(id);
        const template = templateList.find(t => t._id === id);
        if (template) {
            setLetterBody(template.defaultBody || "");
            const initialFields = {};
            (template.fields || []).forEach(f => {
                initialFields[f.key] = "";
            });
            setFieldValues(initialFields);
        }
    };

    const handleFieldChange = (key, value) => {
        setFieldValues(prev => ({ ...prev, [key]: value }));
    };

    const [isEditingDesign, setIsEditingDesign] = useState(false);
    const [designForm, setDesignForm] = useState({});

    const openDesignModal = () => {
        const t = templates.find(t => t._id === selectedTemplateId);
        if (!t) return;
        setDesignForm({
            logoUrl: t.headerConfig?.logoUrl || "",
            logoWidth: t.headerConfig?.logoWidth || 120,
            logoHeight: t.headerConfig?.logoHeight || 60,
            companyName: t.headerConfig?.companyName || "",
            companyAddress: t.headerConfig?.companyAddress || "",
            contactDetails: t.headerConfig?.contactDetails || "",
            alignment: t.headerConfig?.alignment || "split",
            footerText: t.footerConfig?.text || "",
            signatureUrl: t.footerConfig?.signatureUrl || "",
            signatureWidth: t.footerConfig?.signatureWidth || 150,
            signatureHeight: t.footerConfig?.signatureHeight || 60,
        });
        setIsEditingDesign(true);
    };

    const saveDesign = async () => {
        try {
            const t = templates.find(tmpl => tmpl._id === selectedTemplateId);
            const newHeaderConfig = {
                logoUrl: designForm.logoUrl,
                logoWidth: Number(designForm.logoWidth),
                logoHeight: Number(designForm.logoHeight),
                companyName: designForm.companyName,
                companyAddress: designForm.companyAddress,
                contactDetails: designForm.contactDetails,
                alignment: designForm.alignment || "split",
            };
            const newFooterConfig = {
                text: designForm.footerText,
                signatureUrl: designForm.signatureUrl,
                signatureWidth: Number(designForm.signatureWidth),
                signatureHeight: Number(designForm.signatureHeight),
                showPageNumber: t.footerConfig?.showPageNumber ?? true
            };

            // Optimistic Update
            const optimisticTemplate = { ...t, headerConfig: newHeaderConfig, footerConfig: newFooterConfig };
            setTemplates(templates.map(tmpl => tmpl._id === selectedTemplateId ? optimisticTemplate : tmpl));
            setIsEditingDesign(false);
            setMessage("Design saved instantly.");

            // Background Sync
            api.put(`/api/admin/general-letter-templates/${selectedTemplateId}`, {
                headerConfig: newHeaderConfig,
                footerConfig: newFooterConfig
            }).catch(() => setMessage("Failed to sync design to server."));
        } catch (error) {
            setMessage("Failed to save design.");
        }
    };

    const downloadPDF = async () => {
        setGenerating(true);
        setMessage("");
        const template = templates.find(t => t._id === selectedTemplateId);
        if (!template) return;

        try {
            // Generate PDF Blob
            const blob = await pdf(<MultiPagePdfDocument template={template} fieldValues={fieldValues} letterBody={letterBody} />).toBlob();
            
            // Create Object URL and trigger download
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${template.name.replace(/\s+/g, '_')}_${fieldValues.employeeName || 'Letter'}.pdf`;
            a.click();
            URL.revokeObjectURL(url);

            // Optional: Save to backend
            await api.post("/api/admin/generated-letters", {
                templateId: template._id,
                templateType: template.type,
                employeeName: fieldValues.employeeName || 'Unknown',
                fieldValues,
                finalContent: letterBody
            });

            setMessage("Letter generated and downloaded successfully.");
        } catch (error) {
            setMessage("Failed to generate PDF.");
        } finally {
            setGenerating(false);
        }
    };

    const emailPDF = async () => {
        if (!recipientEmail) {
            setMessage("Please enter a Recipient Email.");
            return;
        }

        setGenerating(true);
        setMessage("Generating PDF and sending email...");
        const template = templates.find(t => t._id === selectedTemplateId);
        if (!template) return;

        try {
            // Generate PDF Blob
            const blob = await pdf(<MultiPagePdfDocument template={template} fieldValues={fieldValues} letterBody={letterBody} />).toBlob();
            
            // Convert Blob to Base64
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = async () => {
                try {
                    await api.post("/api/admin/generated-letters/send-email", {
                        templateId: template._id,
                        templateType: template.type,
                        employeeName: fieldValues.employeeName || 'Unknown',
                        employeeEmail: recipientEmail,
                        fieldValues,
                        finalContent: letterBody,
                        pdfBase64: reader.result,
                        fileName: `${template.name.replace(/\s+/g, '_')}_${fieldValues.employeeName || 'Letter'}.pdf`
                    });
                    setMessage("Email sent successfully!");
                } catch (error) {
                    setMessage("Failed to send email.");
                } finally {
                    setGenerating(false);
                }
            };
        } catch (error) {
            setMessage("Failed to generate PDF for emailing.");
            setGenerating(false);
        }
    };

    const activeTemplate = templates.find(t => t._id === selectedTemplateId);

    if (loading) {
        return (
            <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-secondary)] text-slate-950 dark:bg-[#0d0d0f] dark:text-white">
                <AdminNavbar />
                <main className="min-w-0 flex-1 px-4 py-6 lg:px-8 flex items-center justify-center">
                    <RefreshCw className="animate-spin text-slate-400" size={32} />
                </main>
            </div>
        );
    }

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-secondary)] text-slate-950 dark:bg-[#0d0d0f] dark:text-white">
            <AdminNavbar />
            <main className="min-w-0 flex-1 px-4 py-6 lg:px-8 flex flex-col">
                
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-black tracking-tight">General Letters</h1>
                        <p className="mt-2 text-sm text-slate-500">Select a template, fill in the details, and generate a multi-page PDF letter.</p>
                    </div>
                    {/* Add template builder link or button here if needed */}
                </div>

                {message && (
                    <div className="fixed bottom-6 right-6 z-[300] max-w-sm rounded-xl border border-emerald-500/20 bg-emerald-500 text-white px-5 py-3 shadow-2xl flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4">
                        <span className="text-sm font-bold">{message}</span>
                    </div>
                )}

                <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                    
                    {/* LEFT PANEL: Editor */}
                    <div className="w-full lg:w-1/3 flex flex-col gap-6 overflow-y-auto pr-2 pb-10">
                        
                        <div className="rounded-[1.75rem] border border-slate-200 bg-[var(--card-bg)] p-6 shadow-sm dark:border-white/10 dark:bg-[#121214]">
                            <div className="flex flex-col gap-2 mb-4" ref={dropdownRef}>
                                <span className="text-xs font-bold uppercase text-slate-500">Select Template</span>
                                <div className="relative">
                                    <button
                                        type="button"
                                        className="w-full h-11 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[var(--accent-primary)]/20 dark:bg-[#18181b] dark:border-white/10 dark:text-white"
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        <span className="truncate">
                                            {templates.find(t => t._id === selectedTemplateId)?.name || "Select a template..."}
                                        </span>
                                        <ChevronDown size={16} className={`text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {isDropdownOpen && (
                                        <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 shadow-xl dark:bg-[#18181b] dark:border-white/10 max-h-[450px] overflow-y-auto py-1 rounded-xl">
                                            {templates.map(t => (
                                                <button
                                                    key={t._id}
                                                    type="button"
                                                    className={`w-full flex items-center px-4 py-2.5 text-sm text-left transition-colors ${
                                                        t._id === selectedTemplateId
                                                            ? 'bg-[var(--accent-primary)] text-white font-semibold'
                                                            : 'text-[var(--text-primary)] hover:bg-[var(--accent-soft)] hover:text-[var(--brand-dark)] dark:hover:bg-[var(--accent-primary)]/20 dark:hover:text-white font-medium'
                                                    }`}
                                                    onClick={() => {
                                                        selectTemplate(t._id);
                                                        setIsDropdownOpen(false);
                                                    }}
                                                >
                                                    {t.name}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {activeTemplate && (
                                <button 
                                    className="w-full h-10 rounded-xl border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
                                    onClick={openDesignModal}
                                >
                                    Edit Template Header/Footer
                                </button>
                            )}
                        </div>

                        {isEditingDesign && (
                            <div className="fixed inset-0 z-[280] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
                                <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-[1.5rem] bg-[var(--card-bg)] p-6 shadow-2xl dark:bg-[#111113]">
                                    <h2 className="text-xl font-bold mb-4">Edit Design Template</h2>
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold uppercase text-slate-500">Logo Image</span>
                                            <div className="flex items-center gap-3">
                                                {designForm.logoUrl && <img src={designForm.logoUrl} alt="Logo" className="h-10 w-auto object-contain border rounded" />}
                                                <input 
                                                    type="file" 
                                                    accept="image/*"
                                                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => setDesignForm({...designForm, logoUrl: reader.result});
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }} 
                                                />
                                            </div>
                                            <div className="flex gap-4 mt-2">
                                                <label className="flex flex-col gap-1 flex-1">
                                                    <span className="text-[10px] font-bold text-slate-400">WIDTH (px)</span>
                                                    <input type="number" className="h-8 rounded-lg border px-2 text-xs dark:bg-[#18181b] dark:border-white/10" value={designForm.logoWidth} onChange={e => setDesignForm({...designForm, logoWidth: e.target.value})} />
                                                </label>
                                                <label className="flex flex-col gap-1 flex-1">
                                                    <span className="text-[10px] font-bold text-slate-400">HEIGHT (px)</span>
                                                    <input type="number" className="h-8 rounded-lg border px-2 text-xs dark:bg-[#18181b] dark:border-white/10" value={designForm.logoHeight} onChange={e => setDesignForm({...designForm, logoHeight: e.target.value})} />
                                                </label>
                                            </div>
                                        </div>
                                        <label className="flex flex-col gap-1">
                                            <span className="text-xs font-bold uppercase text-slate-500">Company Name</span>
                                            <input type="text" className="h-10 rounded-xl border px-3 text-sm dark:bg-[#18181b] dark:border-white/10" value={designForm.companyName} onChange={e => setDesignForm({...designForm, companyName: e.target.value})} />
                                        </label>
                                        <label className="flex flex-col gap-1">
                                            <span className="text-xs font-bold uppercase text-slate-500">Company Address</span>
                                            <input type="text" className="h-10 rounded-xl border px-3 text-sm dark:bg-[#18181b] dark:border-white/10" value={designForm.companyAddress} onChange={e => setDesignForm({...designForm, companyAddress: e.target.value})} />
                                        </label>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold uppercase text-slate-500">Contact Details</span>
                                            <input type="text" className="h-10 rounded-xl border px-3 text-sm dark:bg-[#18181b] dark:border-white/10" value={designForm.contactDetails} onChange={e => setDesignForm({...designForm, contactDetails: e.target.value})} />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold uppercase text-slate-500">Header Layout Style</span>
                                            <select className="h-10 rounded-xl border px-3 text-sm dark:bg-[#18181b] dark:border-white/10" value={designForm.alignment || 'split'} onChange={e => setDesignForm({...designForm, alignment: e.target.value})}>
                                                <option value="split">Split (Logo Left, Text Right)</option>
                                                <option value="center">Centered (Logo Top, Text Bottom)</option>
                                                <option value="left">Left Aligned (Logo Top, Text Bottom)</option>
                                                <option value="right">Right Aligned (Logo Top, Text Bottom)</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span className="text-xs font-bold uppercase text-slate-500">Signature Image</span>
                                            <div className="flex items-center gap-3">
                                                {designForm.signatureUrl && <img src={designForm.signatureUrl} alt="Signature" className="h-10 w-auto object-contain border rounded" />}
                                                <input 
                                                    type="file" 
                                                    accept="image/*"
                                                    className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/30 dark:file:text-blue-400"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => setDesignForm({...designForm, signatureUrl: reader.result});
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }} 
                                                />
                                            </div>
                                            <div className="flex gap-4 mt-2">
                                                <label className="flex flex-col gap-1 flex-1">
                                                    <span className="text-[10px] font-bold text-slate-400">WIDTH (px)</span>
                                                    <input type="number" className="h-8 rounded-lg border px-2 text-xs dark:bg-[#18181b] dark:border-white/10" value={designForm.signatureWidth} onChange={e => setDesignForm({...designForm, signatureWidth: e.target.value})} />
                                                </label>
                                                <label className="flex flex-col gap-1 flex-1">
                                                    <span className="text-[10px] font-bold text-slate-400">HEIGHT (px)</span>
                                                    <input type="number" className="h-8 rounded-lg border px-2 text-xs dark:bg-[#18181b] dark:border-white/10" value={designForm.signatureHeight} onChange={e => setDesignForm({...designForm, signatureHeight: e.target.value})} />
                                                </label>
                                            </div>
                                        </div>
                                        <label className="flex flex-col gap-1">
                                            <span className="text-xs font-bold uppercase text-slate-500">Footer Text</span>
                                            <input type="text" className="h-10 rounded-xl border px-3 text-sm dark:bg-[#18181b] dark:border-white/10" value={designForm.footerText} onChange={e => setDesignForm({...designForm, footerText: e.target.value})} />
                                        </label>
                                    </div>
                                    <div className="mt-6 flex justify-end gap-3">
                                        <button className="px-4 py-2 font-bold text-slate-500 hover:text-slate-800" onClick={() => setIsEditingDesign(false)}>Cancel</button>
                                        <button className="rounded-xl bg-blue-600 px-5 py-2 font-bold text-white hover:bg-blue-700" onClick={saveDesign}>Save Design</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTemplate && activeTemplate.fields && activeTemplate.fields.length > 0 && (
                            <div className="rounded-[1.75rem] border border-slate-200 bg-[var(--card-bg)] p-6 shadow-sm dark:border-white/10 dark:bg-[#121214]">
                                <h2 className="text-sm font-bold mb-4 uppercase tracking-wide text-slate-500">Employee Details</h2>
                                <div className="grid grid-cols-1 gap-4">
                                    {activeTemplate.fields.map(field => (
                                        <label key={field.key} className="flex flex-col gap-1">
                                            <span className="text-xs font-bold text-slate-600 dark:text-slate-400">{field.label}</span>
                                            <input 
                                                type={field.type === 'date' ? 'date' : 'text'} 
                                                className="h-10 rounded-xl border border-slate-200 px-3 text-sm dark:bg-[#18181b] dark:border-white/10"
                                                value={fieldValues[field.key] || ''}
                                                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                            />
                                        </label>
                                    ))}
                                    <label className="flex flex-col gap-1 mt-4 border-t pt-4 border-slate-100 dark:border-white/10">
                                        <span className="text-xs font-bold text-slate-600 dark:text-slate-400 text-blue-600 dark:text-blue-400">Recipient Email(s)</span>
                                        <span className="text-[10px] text-slate-400 -mt-1 mb-1">Separate multiple emails with commas</span>
                                        <input 
                                            type="text" 
                                            className="h-10 rounded-xl border border-slate-200 px-3 text-sm dark:bg-[#18181b] dark:border-white/10 focus:ring-2 focus:ring-blue-500/20 outline-none"
                                            value={recipientEmail}
                                            onChange={(e) => setRecipientEmail(e.target.value)}
                                            placeholder="employee@example.com, manager@example.com"
                                        />
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="rounded-[1.75rem] border border-slate-200 bg-[var(--card-bg)] p-6 shadow-sm flex-1 dark:border-white/10 dark:bg-[#121214]">
                            <h2 className="text-sm font-bold mb-4 uppercase tracking-wide text-slate-500">Letter Content (Markdown)</h2>
                            <textarea 
                                className="w-full h-64 rounded-xl border border-slate-200 p-3 text-sm font-mono leading-relaxed outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-[#18181b] dark:border-white/10"
                                value={letterBody}
                                onChange={(e) => setLetterBody(e.target.value)}
                                placeholder="Use **text** for bold. Placeholders like {{employeeName}} will be replaced."
                            />

                            <div className="mt-6 flex flex-col sm:flex-row gap-3">
                                <button 
                                    className="flex-1 flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-700 font-bold h-11 hover:bg-slate-50 disabled:opacity-50 dark:bg-transparent dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                                    onClick={() => {
                                        try {
                                            const t = templates.find(tmpl => tmpl._id === selectedTemplateId);
                                            // Optimistic update
                                            const optimisticTemplate = { ...t, body: letterBody, defaultBody: letterBody };
                                            setTemplates(templates.map(tmpl => tmpl._id === selectedTemplateId ? optimisticTemplate : tmpl));
                                            setMessage("Template body saved instantly.");

                                            // Background sync
                                            api.put(`/api/admin/general-letter-templates/${selectedTemplateId}`, {
                                                defaultBody: letterBody,
                                                body: letterBody
                                            }).catch(() => setMessage("Failed to sync template save to server."));
                                        } catch (error) {
                                            setMessage("Failed to save template.");
                                        }
                                    }}
                                    disabled={generating}
                                >
                                    <Save size={18} />
                                    Save Template
                                </button>
                                
                                <button 
                                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-indigo-600 text-white font-bold h-11 hover:bg-indigo-700 disabled:opacity-50"
                                    onClick={emailPDF}
                                    disabled={generating}
                                >
                                    {generating ? <RefreshCw className="animate-spin" size={18} /> : <Send size={18} />}
                                    Email PDF
                                </button>
                                
                                <button 
                                    className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 text-white font-bold h-11 hover:bg-blue-700 disabled:opacity-50"
                                    onClick={downloadPDF}
                                    disabled={generating}
                                >
                                    {generating ? <RefreshCw className="animate-spin" size={18} /> : <Download size={18} />}
                                    Download PDF
                                </button>
                            </div>
                        </div>

                    </div>

                    {/* RIGHT PANEL: Live PDF Preview */}
                    <div className="w-full lg:w-2/3 min-h-[500px] h-[70vh] lg:h-[calc(100vh-140px)] bg-slate-100 rounded-[1.75rem] border border-slate-200 overflow-hidden shadow-inner dark:bg-[#18181b] dark:border-white/10">
                        {activeTemplate ? (
                            <PDFViewer width="100%" height="100%" className="border-none">
                                <MultiPagePdfDocument 
                                    template={activeTemplate} 
                                    fieldValues={fieldValues} 
                                    letterBody={letterBody} 
                                />
                            </PDFViewer>
                        ) : (
                            <div className="flex h-full items-center justify-center text-slate-400">
                                Select a template to preview PDF
                            </div>
                        )}
                    </div>

                </div>
            </main>
        </div>
    );
};

export default GeneralLetters;
